// Zustand der Huelle selbst (docs/shell.md).
//
// Nicht der INHALT (das sind die Tabs in tabs.svelte.ts), sondern die
// Moebel: welche Sektion die Seitenleiste zeigt, ob sie offen ist, ob
// das untere Panel offen ist und welcher Reiter dort vorne liegt.
//
// Alles landet in den Prefs: die Einrichtung des Arbeitsplatzes gehoert
// dem Nutzer und soll den Reload ueberleben -- genauso wie der
// Tab-Stapel.

import { getGeraetPref, prefsGeladen, setGeraetPref } from "../prefs.svelte";
import type { SectionId } from "../nav-sections";

// "ereignisse" gab es bis 15.10.2026: der Reiter verwies nur auf die Seite
// (Tim: „Ich schaue hier nie nach"). Ein so gemerkter Reiter faellt beim
// Herstellen auf den Standard zurueck.
export type PanelReiter = "meldungen" | "laeufe" | "terminal";
const REITER: readonly PanelReiter[] = ["meldungen", "laeufe", "terminal"];

interface ChromeZustand {
  sektion: SectionId;
  seitenleiste: boolean;
  seitenleisteBreite: number;
  panel: boolean;
  reiter: PanelReiter;
  panelHoehe: number;
  /** Reihenfolge der Bereiche in der Aktivitätsleiste (Tim 19.10.2026: „Die
   *  Elemente in der Seitenleiste müssen noch verschiebbar sein"); ohne =
   *  wie navSections. */
  bereiche?: string[];
}

const PREF_KEY = "shell_chrome";

const standard: ChromeZustand = {
  sektion: "infra",
  seitenleiste: true,
  seitenleisteBreite: 264,
  panel: false,
  reiter: "meldungen",
  panelHoehe: 220,
};

let zustand = $state<ChromeZustand>({ ...standard });
let hergestellt = false;

/** stelleChromeHer laedt die gemerkte Einrichtung. Einmal beim Start --
 *  spaetere Aufrufe sind no-op, SOBALD wirklich etwas hergestellt wurde.
 *
 *  Warum nicht schon beim ersten Aufruf gesperrt wird: auf einem
 *  Browser ohne warmen localStorage-Cache laeuft der Mount, bevor der
 *  Server-Stand da ist. "Nichts gefunden" heisst dann nicht "nichts
 *  gemerkt". Die Shell ruft deshalb noch einmal, sobald prefsGeladen()
 *  steht (21.08.). */
export function stelleChromeHer(): void {
  if (hergestellt) return;
  const gemerkt = getGeraetPref<Partial<ChromeZustand>>(PREF_KEY);
  if (!gemerkt) {
    // Kein Wert UND kein Server-Stand: spaeter nochmal fragen.
    if (prefsGeladen()) hergestellt = true;
    return;
  }
  hergestellt = true;
  zustand = {
    sektion: gemerkt.sektion ?? standard.sektion,
    seitenleiste: gemerkt.seitenleiste ?? standard.seitenleiste,
    seitenleisteBreite: Math.min(
      560,
      Math.max(160, gemerkt.seitenleisteBreite ?? standard.seitenleisteBreite),
    ),
    panel: gemerkt.panel ?? standard.panel,
    reiter: gemerkt.reiter && REITER.includes(gemerkt.reiter) ? gemerkt.reiter : standard.reiter,
    // Grenzen auch beim Herstellen: ein aus einer alten Version
    // gemerkter Unsinn darf die Flaeche nicht auffressen.
    panelHoehe: Math.min(600, Math.max(120, gemerkt.panelHoehe ?? standard.panelHoehe)),
    bereiche: Array.isArray(gemerkt.bereiche) ? gemerkt.bereiche.filter((x) => typeof x === "string") : undefined,
  };
}

function merke(): void {
  // Vor dem Server-Stand nichts schreiben -- ein leerer Anfangszustand
  // wuerde die gemerkte Einrichtung ueberschreiben (siehe merkeLayout
  // in tabs.svelte.ts, gleicher Fall). Der Griff des Nutzers gilt aber
  // ab jetzt als gesetzt: ein spaeter eintreffender Server-Wert soll
  // ihn nicht wieder wegdruecken.
  hergestellt = true;
  if (!prefsGeladen()) return;
  setGeraetPref(PREF_KEY, { ...zustand });
}

export function sichtbareSektion(): SectionId {
  return zustand.sektion;
}
export function seitenleisteOffen(): boolean {
  return zustand.seitenleiste && weggelegt === null;
}

// Eine Seite kann den Baum vorübergehend weglegen (Konstruieren mit Claude,
// docs/konstruktionen.md) -- ohne die gemerkte Einrichtung anzufassen:
// nichts davon wird gespeichert. Erst wurde dafür schalteSeitenleiste()
// benutzt; das speicherte „zu", und lud die Seite neu, während die
// Konstruktion vorn stand, blieb der Baum überall weg (Tim 19.10.2026: „Der
// Tree ist jetzt immer weg"). Jede Geste (Symbol, Strg+B, Link auf einen
// Bereich) holt ihn sofort zurück.
let weggelegt = $state<string | null>(null);

export function legeBaumWeg(von: string): void {
  weggelegt = von;
}

/** Nur wer ihn weggelegt hat, legt ihn zurück. */
export function holeBaumZurueck(von: string): void {
  if (weggelegt === von) weggelegt = null;
}
export function panelOffen(): boolean {
  return zustand.panel;
}
export function panelReiter(): PanelReiter {
  return zustand.reiter;
}
export function panelHoehe(): number {
  return zustand.panelHoehe;
}
export function seitenleisteBreite(): number {
  return zustand.seitenleisteBreite;
}

/** Gemerkte Reihenfolge der Bereiche in der Aktivitätsleiste. */
export function bereichReihe(): readonly string[] | undefined {
  return zustand.bereiche;
}

/** undefined = zurück zur Reihenfolge von navSections. */
export function setzeBereichReihe(ids: string[] | undefined): void {
  zustand.bereiche = ids;
  merke();
}

/** Grenzen, damit die Leiste weder verschwindet noch die Arbeitsflaeche
 *  auffrisst. Wer sie ganz weg haben will, klappt sie zu (Strg+B). */
export function setzeSeitenleisteBreite(px: number): void {
  zustand.seitenleisteBreite = Math.min(560, Math.max(160, Math.round(px)));
  merke();
}

/** zeigeSektion schaltet die Seitenleiste um. Dieselbe Sektion noch
 *  einmal anzuklicken klappt sie zu -- die Geste, die man in jeder IDE
 *  ausprobiert, wenn man Platz braucht. */
export function zeigeSektion(id: SectionId): void {
  if (weggelegt !== null) {
    // weggelegt heißt nicht „zugeklappt": der Klick will ihn sehen
    weggelegt = null;
    zustand.sektion = id;
    zustand.seitenleiste = true;
    merke();
    return;
  }
  if (zustand.sektion === id && zustand.seitenleiste) {
    zustand.seitenleiste = false;
  } else {
    zustand.sektion = id;
    zustand.seitenleiste = true;
  }
  merke();
}

/** oeffneSektion zeigt den Baum einer Sektion -- ohne Umschalten: ein
 *  Link auf einen Bereich (/infra) soll ihn zeigen, nicht zuklappen. */
export function oeffneSektion(id: SectionId): void {
  weggelegt = null;
  zustand.sektion = id;
  zustand.seitenleiste = true;
  merke();
}

/** folgeSektion zieht die Seitenleiste dem aktiven Tab nach.
 *
 *  Anders als zeigeSektion(): kein Umschalt-Verhalten, und die
 *  Seitenleiste wird NICHT aufgeklappt, wenn sie zu ist -- wer sie
 *  zugemacht hat, wollte Platz, und ein Tab-Wechsel ist kein Grund,
 *  ihm den wieder wegzunehmen (Tim 18.08.).
 *
 *  Ein Klick in der Aktivitaetsleiste gewinnt bis zum naechsten
 *  Tab-Wechsel: der Aufrufer haengt das hier an die Tab-Identitaet,
 *  nicht an die Sektion. */
export function folgeSektion(id: SectionId): void {
  if (zustand.sektion === id) return;
  zustand.sektion = id;
  merke();
}

export function schalteSeitenleiste(an?: boolean): void {
  if (weggelegt !== null) {
    weggelegt = null;
    zustand.seitenleiste = an ?? true;
  } else {
    zustand.seitenleiste = an ?? !zustand.seitenleiste;
  }
  merke();
}

export function schaltePanel(an?: boolean): void {
  zustand.panel = an ?? !zustand.panel;
  merke();
}

export function zeigeReiter(r: PanelReiter): void {
  zustand.reiter = r;
  zustand.panel = true;
  merke();
}

export function setzePanelHoehe(px: number): void {
  zustand.panelHoehe = Math.min(600, Math.max(120, Math.round(px)));
  merke();
}
